/**
 * ProfileRelevantTagsSection — sortable table of Phyllo's profile-relevant
 * keywords with relevance + usage scores. Top 10 by default, expand to
 * show full set.
 *
 * Hidden when tags array is empty.
 */
import { useMemo, useState } from 'react';
import type { InsightIQProfile } from '@discovr/types';
import { SectionTitle } from '../charts/StatCard';
import { fmtDecimal2 } from '../utils/formatters';

interface ProfileRelevantTagsSectionProps {
  profile: InsightIQProfile;
}

const COLLAPSED_LIMIT = 10;

export function ProfileRelevantTagsSection({ profile }: ProfileRelevantTagsSectionProps) {
  // Memoized so the `?? []` fallback doesn't hand `sorted` a fresh array
  // reference every render (which would defeat its useMemo below).
  const tags = useMemo(
    () => profile.profile_relevant_tags ?? [],
    [profile.profile_relevant_tags],
  );
  const [expanded, setExpanded] = useState(false);

  const sorted = useMemo(
    () =>
      [...tags].sort(
        (a, b) => (b.relevance_score ?? 0) - (a.relevance_score ?? 0),
      ),
    [tags],
  );

  if (sorted.length === 0) return null;

  const visible = expanded ? sorted : sorted.slice(0, COLLAPSED_LIMIT);

  return (
    <div className="rounded-2xl border border-border bg-card p-5 mb-4">
      <SectionTitle>Profile Relevant Tags</SectionTitle>
      <div className="mt-3 overflow-hidden rounded-lg border border-border">
        <table className="w-full text-left text-caption-regular">
          <thead className="bg-muted text-[12px] font-semibold uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-3 py-2">Keyword</th>
              <th className="px-3 py-2 text-right">Relevance</th>
              <th className="px-3 py-2 text-right">Usage</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((t) => (
              <tr key={t.keyword} className="border-t border-border">
                <td className="px-3 py-2 font-medium text-foreground">{t.keyword}</td>
                <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                  {fmtDecimal2(t.relevance_score) ?? '—'}
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                  {fmtDecimal2(t.usage_score) ?? '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {sorted.length > COLLAPSED_LIMIT && (
        <button
          onClick={() => setExpanded((e) => !e)}
          className="mt-2 text-[12px] font-semibold text-blue-600 hover:text-blue-800"
        >
          {expanded ? 'Show fewer' : `Show all ${sorted.length}`}
        </button>
      )}
    </div>
  );
}
